import { setStyle } from '@remirror/core';
import type { EditorView, NodeView, ProsemirrorNode } from '@remirror/pm';
import type { ImageAttributes } from './interfaces.image';

// Helper function to detect if a file is a video based on its URL
function isVideoFile(src: string): boolean {
  if (!src) return false;

  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.m4v', '.mkv', '.wmv', '.flv'];
  const lowercaseSrc = src.toLowerCase();

  return videoExtensions.some(ext => lowercaseSrc.includes(ext)) ||
         lowercaseSrc.includes('video/');
}

export type DisplayOptions = {
  sizeOverrides?: {
    width: string;
    height: string;
  };
};
export class DisplayView implements NodeView {
  dom: HTMLElement;
  private image: HTMLImageElement | HTMLVideoElement;
  private dialogImage: HTMLImageElement | HTMLVideoElement;

  constructor(node: ProsemirrorNode, view: EditorView, getPos: () => number, options: DisplayOptions = {}) {
    const attrs = node.attrs as ImageAttributes;
    this.dom = document.createElement('div');

    this.image = createImage(options.sizeOverrides || {
      width: !isNaN(+attrs.width) ? `${attrs.width}px` : attrs.width.toString(),
      height: !isNaN(+attrs.height) ? `${attrs.height}px` : attrs.height.toString(),
    });
    this.dom.appendChild(this.image);

    const dialog = document.createElement('dialog');
    this.dialogImage = createImage({
      maxHeight: 'calc(100vh - 100px)',
      maxWidth: 'calc(100vw - 100px)',
    });
    dialog.appendChild(this.dialogImage);
    dialog.onclick = e => {
      if (e.target === dialog) {
        dialog.close();
      }

      e.stopPropagation();
      return false;
    };

    this.dom.appendChild(dialog);

    // Only enable modal for images, not videos
    if (!(this.image instanceof HTMLVideoElement)) {
      this.image.onclick = e => {
        dialog.showModal();
        e.stopPropagation();

        return false;
      };
    }

    function createImage(style: Partial<CSSStyleDeclaration>) {
      const src = attrs.src;
      const isVideo = isVideoFile(src);

      if (isVideo) {
        const video = document.createElement('video');
        video.setAttribute('src', attrs.src);
        video.setAttribute('data-cid', attrs.cid as string);
        video.setAttribute('controls', 'true');
        video.setAttribute('preload', 'metadata');
        video.setAttribute('autoplay', 'true');
        video.setAttribute('muted', 'true');
        video.setAttribute('loop', 'true');

        if (style) {
          setStyle(video, {
            maxWidth: '400px',
            maxHeight: '300px',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: '5px',
            ...style,
          });
        }
        return video;
      } else {
        const img = document.createElement('img');
        img.setAttribute('src', attrs.src);
        img.setAttribute('data-cid', attrs.cid as string);
        if (style) {
          setStyle(img, style);
        }
        return img;
      }
    }
  }

  update(node: ProsemirrorNode): boolean {
    if (node.type !== node.type.schema.nodes.image) {
      return false;
    }

    updateImage(this.image);
    updateImage(this.dialogImage);

    return true;

    function updateImage(element: HTMLImageElement | HTMLVideoElement) {
      element.setAttribute('src', node.attrs.src as string);
      element.setAttribute('data-cid', node.attrs.cid as string);
    }
  }

  destroy(): void {
    this.dom.remove();
  }

  stopEvent(): boolean {
    return true;
  }
}